/**
 * V2 Services catalog — static bilingual source of record (Phase 5).
 *
 * Rules for this file:
 * - Every consumer imports ONLY through /services/v2ServicesRepository.js.
 * - Never import this array directly into a page/component.
 * - Later, the repository's internals swap to `fetch('/api/public/services')`
 *   without changing this schema or the repository's public contract.
 *
 * Schema (locked so an admin-managed store can replace this file cleanly):
 *   {
 *     id:                 stable identifier — never edited after publish
 *     slug:               URL segment for /v2/services/:slug (editable for SEO)
 *     status:             'published' | 'draft' | 'archived'
 *     order:              numeric display order (ascending)
 *     featured:           boolean
 *     kicker:             { en, es } — optional supporting phrase (may be '')
 *     title:              { en, es }
 *     short_description: { en, es }
 *     overview:           { en, es }
 *     ideal_for:          { en, es }
 *     problems_addressed: { en: [str], es: [str] }
 *     deliverables:       { en: [str], es: [str] }
 *     outcomes:           { en: [str], es: [str] }
 *     pricing:            { en, es }
 *     cta_label:          { en, es }
 *     related_service_ids: [id, id, ...]
 *     seo: { title: {en,es}, description: {en,es} }
 *   }
 */

const V2_SERVICES = [
  {
    id: 'service_business_systems_audit',
    slug: 'business-systems-audit',
    status: 'published',
    order: 10,
    featured: true,
    kicker: { en: 'Clarity, shipped.', es: 'Claridad, entregada.' },
    title: {
      en: 'Business Systems Audit',
      es: 'Auditoría de Sistemas de Negocio',
    },
    short_description: {
      en: 'A focused review of your current systems, operational friction, and priorities, resulting in a practical 90-day action plan.',
      es: 'Una revisión enfocada de tus sistemas actuales, los puntos de fricción operativa y las prioridades, con un plan de acción práctico a 90 días.',
    },
    overview: {
      en: 'We look at how your business actually runs — the tools, handoffs, repeated tasks, and workarounds — and translate what we find into clear decisions. The audit is focused, structured, and specific to your operation. You leave with a written 90-day plan and a shared understanding of where change can create the greatest impact.',
      es: 'Analizamos cómo funciona realmente tu negocio: las herramientas, los traspasos, las tareas repetidas y las soluciones improvisadas. Después, convertimos lo que encontramos en decisiones claras. La auditoría es enfocada, estructurada y específica para tu operación. Al finalizar, tendrás un plan escrito a 90 días y una visión compartida de dónde los cambios pueden generar el mayor impacto.',
    },
    ideal_for: {
      en: 'Founders and small operating teams who feel the friction but need an outside perspective before investing in a new system, hiring additional support, or adopting another tool.',
      es: 'Fundadores y equipos operativos pequeños que sienten la fricción, pero necesitan una perspectiva externa antes de invertir en un sistema nuevo, contratar apoyo adicional o adoptar otra herramienta.',
    },
    problems_addressed: {
      en: [
        'Repeated work between tools that should talk to each other',
        'Data lives in more places than the team can track',
        'The team does not have a shared view of which improvement should come first',
        'Growth is being slowed by systems, not by demand',
      ],
      es: [
        'Trabajo repetido entre herramientas que deberían comunicarse',
        'La información vive en más lugares de los que el equipo puede seguir',
        'El equipo no tiene una visión compartida de cuál mejora debe hacerse primero',
        'El crecimiento se frena por los sistemas, no por la demanda',
      ],
    },
    deliverables: {
      en: [
        'A working session covering the eight core systems: strategy, brand, customer journey, operations, data, automation, AI readiness, and bilingual reach',
        'System health scoring and a plain-language written report',
        'A prioritized 90-day action plan (30 / 60 / 90-day phases)',
        'A follow-up review to answer questions and confirm next steps',
      ],
      es: [
        'Una sesión de trabajo que cubre los ocho sistemas base: estrategia, marca, experiencia del cliente, operaciones, datos, automatización, preparación para IA y alcance bilingüe',
        'Puntuación de salud de sistemas e informe escrito en lenguaje claro',
        'Un plan de acción a 90 días priorizado (fases de 30 / 60 / 90 días)',
        'Una revisión de seguimiento para resolver preguntas y confirmar los próximos pasos',
      ],
    },
    outcomes: {
      en: [
        'A written record of how the business runs today',
        'A short list of the highest-impact priorities',
        'A concrete plan the team can execute with or without us',
      ],
      es: [
        'Un registro escrito de cómo opera hoy el negocio',
        'Una lista corta de las prioridades de mayor impacto',
        'Un plan concreto que el equipo puede ejecutar con o sin nosotros',
      ],
    },
    pricing: {
      en: 'Typical investment: $500–$3,000+. Final scope depends on business size, locations, users, workflow complexity, documentation needs, bilingual requirements, and the depth of the audit.',
      es: 'Inversión típica: $500–$3,000+. El alcance final depende del tamaño del negocio, ubicaciones, usuarios, complejidad del flujo, necesidades de documentación, requisitos bilingües y la profundidad de la auditoría.',
    },
    cta_label: { en: 'Request your audit', es: 'Solicita tu auditoría' },
    related_service_ids: [
      'service_workflow_automation',
      'service_custom_business_systems',
      'service_ai_operational_integration',
    ],
    seo: {
      title: {
        en: 'Business Systems Audit — illumi.dev',
        es: 'Auditoría de Sistemas de Negocio — illumi.dev',
      },
      description: {
        en: 'A focused review of your operational systems with a written 90-day action plan. Typical investment $500–$3,000+.',
        es: 'Una revisión enfocada de tus sistemas operativos con un plan de acción escrito a 90 días. Inversión típica $500–$3,000+.',
      },
    },
  },

  {
    id: 'service_workflow_automation',
    slug: 'workflow-automation',
    status: 'published',
    order: 20,
    featured: true,
    kicker: { en: '', es: '' },
    title: { en: 'Workflow Automation', es: 'Automatización de Flujos de Trabajo' },
    short_description: {
      en: 'Remove the manual handoffs between the tools your business already runs on.',
      es: 'Elimina los traspasos manuales entre las herramientas que tu negocio ya utiliza.',
    },
    overview: {
      en: 'Much of the operational drag in a business comes from work being moved manually between systems that could communicate directly. We map the workflow, choose the right connection method for the tools you already use, and automate only the steps where it creates a clear benefit. The goal is fewer repeated tasks and fewer details the team has to remember.',
      es: 'Gran parte de la fricción operativa viene de mover trabajo manualmente entre sistemas que podrían comunicarse de forma directa. Mapeamos el flujo, elegimos la mejor forma de conectar las herramientas que ya utilizas y automatizamos solo los pasos donde existe un beneficio claro. La meta es reducir las tareas repetidas y la cantidad de detalles que el equipo tiene que recordar.',
    },
    ideal_for: {
      en: 'Teams whose people spend too much time moving information, updating systems, and managing repetitive steps instead of focusing on higher-value work.',
      es: 'Equipos cuyas personas dedican demasiado tiempo a mover información, actualizar sistemas y administrar pasos repetitivos en lugar de concentrarse en trabajo de mayor valor.',
    },
    problems_addressed: {
      en: [
        'Copy-paste between CRM, email, spreadsheets, and ops tools',
        'Manual reporting that steals hours every week',
        "Notifications that don't reach the right person at the right time",
        'Data quality that degrades every time a human retypes it',
      ],
      es: [
        'Copiar y pegar entre CRM, correo, hojas de cálculo y herramientas operativas',
        'Reportes manuales que consumen horas cada semana',
        'Notificaciones que no llegan a la persona correcta en el momento correcto',
        'Calidad de datos que se degrada cada vez que una persona la reescribe',
      ],
    },
    deliverables: {
      en: [
        'Workflow mapping session with the people who actually run it',
        'Selection of the right connectors, scripts, or integrations for your current tools',
        'Implementation, testing, and a written runbook so the team can maintain it',
        'Handoff review with training tailored to how the team already works',
      ],
      es: [
        'Sesión de mapeo del flujo con las personas que realmente lo ejecutan',
        'Selección de los conectores, scripts o integraciones adecuados para tus herramientas actuales',
        'Implementación, pruebas y un manual escrito para que el equipo lo pueda mantener',
        'Revisión de entrega con capacitación adaptada a cómo trabaja el equipo',
      ],
    },
    outcomes: {
      en: [
        'Manual steps removed from documented workflows',
        'Faster handoffs between people and systems',
        'A runbook the team can maintain without us',
      ],
      es: [
        'Pasos manuales eliminados de flujos documentados',
        'Traspasos más rápidos entre personas y sistemas',
        'Un manual que el equipo puede mantener sin nosotros',
      ],
    },
    pricing: {
      en: 'Focused Workflow Sprints typically range from $750–$2,500+. Larger automation engagements are scoped separately according to workflow count, integrations, users, locations, data handling, and implementation requirements.',
      es: 'Los proyectos enfocados de automatización, o Workflow Sprints, generalmente van de $750–$2,500+. Los proyectos de automatización más amplios se dimensionan por separado según el número de flujos, integraciones, usuarios, ubicaciones, manejo de datos y requisitos de implementación.',
    },
    cta_label: {
      en: 'Start with a Business Systems Audit',
      es: 'Comenzar con una Auditoría de Sistemas de Negocio',
    },
    related_service_ids: [
      'service_business_systems_audit',
      'service_custom_business_systems',
      'service_ai_operational_integration',
    ],
    seo: {
      title: { en: 'Workflow Automation — illumi.dev', es: 'Automatización de Flujos de Trabajo — illumi.dev' },
      description: {
        en: 'Remove manual handoffs between your existing tools. Focused Workflow Sprints from $750–$2,500+.',
        es: 'Elimina los traspasos manuales entre tus herramientas actuales. Workflow Sprints enfocados desde $750–$2,500+.',
      },
    },
  },

  {
    id: 'service_custom_business_systems',
    slug: 'custom-business-systems',
    status: 'published',
    order: 30,
    featured: true,
    kicker: { en: 'Build what matters.', es: 'Construye lo que importa.' },
    title: { en: 'Custom Business Systems', es: 'Sistemas de Negocio a Medida' },
    short_description: {
      en: 'Purpose-built internal tools for operations that no longer fit inside off-the-shelf software.',
      es: 'Herramientas internas hechas a medida para operaciones que ya no encajan en el software estándar.',
    },
    overview: {
      en: 'When a business has outgrown its spreadsheets, or when the software it already uses still leaves important gaps, a focused custom system may be the better fit. We design and build practical tools — dashboards, internal applications, and operational databases — that reflect how the team actually works.',
      es: 'Cuando un negocio ha superado sus hojas de cálculo, o cuando el software que ya utiliza sigue dejando vacíos importantes, un sistema personalizado y enfocado puede ser una mejor opción. Diseñamos y construimos herramientas prácticas — tableros, aplicaciones internas y bases de datos operativas — que reflejan cómo trabaja realmente el equipo.',
    },
    ideal_for: {
      en: 'Businesses that have outgrown spreadsheets or reached the limits of what their current subscription software can support.',
      es: 'Negocios que han superado las hojas de cálculo o alcanzaron los límites de lo que su software actual por suscripción puede soportar.',
    },
    problems_addressed: {
      en: [
        'Critical operations run in fragile spreadsheets',
        'A subscription tool "almost" fits but forces the team to work around it',
        'Reporting requires manual assembly every time it is needed',
        'The right data exists somewhere but not where decisions get made',
      ],
      es: [
        'Operaciones críticas viven en hojas de cálculo frágiles',
        'Una herramienta por suscripción "casi" encaja pero obliga al equipo a trabajar alrededor de ella',
        'Los reportes requieren armarse a mano cada vez que se necesitan',
        'Los datos correctos existen en algún lugar, pero no donde se toman las decisiones',
      ],
    },
    deliverables: {
      en: [
        'System scope, data model, and UI shape agreed with the team',
        'Delivery in short, visible increments',
        'Documentation, access setup, and a written handoff',
        'Follow-through review after real use',
      ],
      es: [
        'Alcance del sistema, modelo de datos y forma de la interfaz acordados con el equipo',
        'Entrega en incrementos cortos y visibles',
        'Documentación, configuración de accesos y una entrega escrita',
        'Revisión de seguimiento tras el uso real',
      ],
    },
    outcomes: {
      en: [
        'A system the team actually uses because it fits their work',
        'Fewer critical spreadsheets and fewer workarounds',
        'A documented base that can be extended without rebuilding',
      ],
      es: [
        'Un sistema que el equipo realmente usa porque encaja con su trabajo',
        'Menos operaciones críticas en hojas de cálculo y menos soluciones improvisadas',
        'Una base documentada que se puede extender sin reconstruir',
      ],
    },
    pricing: {
      en: 'Engagements are scoped to your business size, users, locations, workflow complexity, integrations, bilingual requirements, and implementation needs. Start with a Business Systems Audit so we can understand the operation, identify the right priorities, and recommend an appropriate scope.',
      es: 'Cada compromiso se dimensiona según el tamaño del negocio, usuarios, ubicaciones, complejidad del flujo, integraciones, requisitos bilingües y necesidades de implementación. Comienza con una Auditoría de Sistemas de Negocio para entender la operación, identificar las prioridades correctas y recomendar un alcance adecuado.',
    },
    cta_label: {
      en: 'Start with a Business Systems Audit',
      es: 'Comenzar con una Auditoría de Sistemas de Negocio',
    },
    related_service_ids: [
      'service_business_systems_audit',
      'service_workflow_automation',
      'service_bilingual_workflow_design',
    ],
    seo: {
      title: { en: 'Custom Business Systems — illumi.dev', es: 'Sistemas de Negocio a Medida — illumi.dev' },
      description: {
        en: 'Purpose-built internal tools when off-the-shelf software cannot fit your operation.',
        es: 'Herramientas internas hechas a medida cuando el software estándar no encaja con tu operación.',
      },
    },
  },

  {
    id: 'service_bilingual_workflow_design',
    slug: 'bilingual-workflow-design',
    status: 'published',
    order: 40,
    featured: true,
    kicker: { en: 'Systems that travel.', es: 'Sistemas que viajan.' },
    title: { en: 'Bilingual Workflow Design', es: 'Diseño de Flujos Bilingües' },
    short_description: {
      en: 'Make English and Spanish equally usable across team operations, customer journeys, communications, and digital tools.',
      es: 'Haz que el inglés y el español funcionen por igual en las operaciones del equipo, la experiencia del cliente, las comunicaciones y las herramientas digitales.',
    },
    overview: {
      en: 'Bilingual service breaks down when Spanish is added only after a process or system is already built. We design workflows, forms, communications, and internal tools so English and Spanish work together from the beginning — for the team using them and for the customers they serve.',
      es: 'El servicio bilingüe pierde calidad cuando el español se agrega después de que un proceso o sistema ya fue creado. Diseñamos flujos, formularios, comunicaciones y herramientas internas para que el inglés y el español funcionen juntos desde el inicio, tanto para el equipo que los utiliza como para los clientes que atiende.',
    },
    ideal_for: {
      en: 'Businesses serving English- and Spanish-speaking customers or operating with bilingual teams, where language is currently handled inconsistently or added too late.',
      es: 'Negocios que atienden a clientes de habla inglesa y española, o que operan con equipos bilingües, donde el idioma actualmente se maneja de forma inconsistente o se incorpora demasiado tarde.',
    },
    problems_addressed: {
      en: [
        'Customer-facing content is translated inconsistently or after the fact',
        'Internal tools force team members to code-switch to work efficiently',
        'Automated messages sound machine-translated to Spanish-first customers',
        'Bilingual work is treated as a nice-to-have rather than as core operation',
      ],
      es: [
        'El contenido dirigido a clientes se traduce de forma inconsistente o se adapta después de que el proceso ya fue creado',
        'Las herramientas internas obligan al equipo a alternar idiomas para trabajar con fluidez',
        'Los mensajes automáticos suenan traducidos por máquina a los clientes hispanohablantes',
        'El trabajo bilingüe se trata como un extra en vez de operación esencial',
      ],
    },
    deliverables: {
      en: [
        'Bilingual audit of workflows, forms, communications, and internal tools',
        'A working glossary and voice guide for both languages',
        'Redesign of the touchpoints where language quality matters most',
        'Handoff with guidance the team can maintain',
      ],
      es: [
        'Auditoría bilingüe de flujos, formularios, comunicaciones y herramientas internas',
        'Un glosario y guía de voz de trabajo para ambos idiomas',
        'Rediseño de los puntos de contacto donde la calidad del idioma importa más',
        'Entrega con lineamientos que el equipo puede mantener',
      ],
    },
    outcomes: {
      en: [
        'Content and tools that feel clear and natural in both languages',
        'A team that can work in either language with less friction',
        'Documentation that helps maintain bilingual consistency as systems change',
      ],
      es: [
        'Contenido y herramientas que se sienten claros y naturales en ambos idiomas',
        'Un equipo que puede trabajar en cualquiera de los dos idiomas con menos fricción',
        'Documentación que ayuda a mantener la consistencia bilingüe conforme cambian los sistemas',
      ],
    },
    pricing: {
      en: 'Engagements are scoped to your business size, users, locations, workflow complexity, integrations, bilingual requirements, and implementation needs. Start with a Business Systems Audit so we can understand the operation, identify the right priorities, and recommend an appropriate scope.',
      es: 'Cada compromiso se dimensiona según el tamaño del negocio, usuarios, ubicaciones, complejidad del flujo, integraciones, requisitos bilingües y necesidades de implementación. Comienza con una Auditoría de Sistemas de Negocio para entender la operación, identificar las prioridades correctas y recomendar un alcance adecuado.',
    },
    cta_label: {
      en: 'Start with a Business Systems Audit',
      es: 'Comenzar con una Auditoría de Sistemas de Negocio',
    },
    related_service_ids: [
      'service_business_systems_audit',
      'service_custom_business_systems',
      'service_workflow_automation',
    ],
    seo: {
      title: { en: 'Bilingual Workflow Design — illumi.dev', es: 'Diseño de Flujos Bilingües — illumi.dev' },
      description: {
        en: 'Make EN/ES operations first-class inside your team, customer flows, and content.',
        es: 'Hacer que la operación EN/ES sea de primera clase dentro del equipo, los flujos de cliente y el contenido.',
      },
    },
  },

  {
    id: 'service_ai_operational_integration',
    slug: 'ai-operational-integration',
    status: 'published',
    order: 50,
    featured: true,
    kicker: { en: '', es: '' },
    title: { en: 'AI & Operational Integration', es: 'IA e Integración Operativa' },
    short_description: {
      en: 'Add responsible AI to specific parts of the operation where it provides a clear, practical benefit.',
      es: 'Integra IA responsable en partes específicas de la operación donde aporte un beneficio claro y práctico.',
    },
    overview: {
      en: 'We use AI where it provides a defined operational benefit — summarizing information, organizing unstructured input, assisting with bilingual communications, or sorting incoming work. We avoid applying it to decisions or processes where human judgment must remain central. Every integration is scoped, reviewed, and documented so the team understands what the system does, what it does not do, and where human review is required.',
      es: 'Utilizamos IA donde ofrece un beneficio operativo definido: resumir información, organizar contenido no estructurado, apoyar comunicaciones bilingües o clasificar trabajo entrante. Evitamos aplicarla a decisiones o procesos donde el criterio humano debe seguir siendo central. Cada integración se define, revisa y documenta para que el equipo entienda qué hace el sistema, qué no hace y dónde se requiere revisión humana.',
    },
    ideal_for: {
      en: 'Businesses that want to apply AI to a specific operational need rather than adopt it without a defined purpose.',
      es: 'Negocios que quieren aplicar IA a una necesidad operativa específica, en lugar de adoptarla sin un propósito definido.',
    },
    problems_addressed: {
      en: [
        'Teams drowning in unstructured input (emails, notes, documents)',
        'Communication load that grows faster than headcount',
        'Repeated summarization and drafting that could be assisted responsibly',
        'A general sense that AI should be part of the operation but no clear place to start',
      ],
      es: [
        'Equipos ahogados en información no estructurada (correos, notas, documentos)',
        'Carga de comunicación que crece más rápido que el personal',
        'Resúmenes y redacción repetidos que podrían asistirse de forma responsable',
        'Una sensación general de que la IA debería formar parte de la operación pero sin un lugar claro para empezar',
      ],
    },
    deliverables: {
      en: [
        'Identification of the operational places where AI actually helps',
        'Selection of the right model or service for the use case',
        'Implementation with human review, clear logging, defined limits, and conservative default behavior',
        'Written policy and handoff so the team knows what AI does and does not do',
      ],
      es: [
        'Identificación de los lugares operativos donde la IA realmente ayuda',
        'Selección del modelo o servicio adecuado para el caso de uso',
        'Implementación con revisión humana, registros claros, límites definidos y configuraciones iniciales prudentes',
        'Política escrita y entrega para que el equipo sepa qué hace y qué no hace la IA',
      ],
    },
    outcomes: {
      en: [
        'AI applied to specific operational points instead of everywhere at once',
        'A team that understands how it works and where the limits are',
        'Documented safeguards so the practice can scale responsibly',
      ],
      es: [
        'IA aplicada en puntos operativos específicos en vez de en todo a la vez',
        'Un equipo que entiende cómo funciona y dónde están los límites',
        'Salvaguardas documentadas para que la práctica pueda escalar de forma responsable',
      ],
    },
    pricing: {
      en: 'Engagements are scoped to your business size, users, locations, workflow complexity, integrations, bilingual requirements, and implementation needs. Start with a Business Systems Audit so we can understand the operation, identify the right priorities, and recommend an appropriate scope.',
      es: 'Cada compromiso se dimensiona según el tamaño del negocio, usuarios, ubicaciones, complejidad del flujo, integraciones, requisitos bilingües y necesidades de implementación. Comienza con una Auditoría de Sistemas de Negocio para entender la operación, identificar las prioridades correctas y recomendar un alcance adecuado.',
    },
    cta_label: {
      en: 'Start with a Business Systems Audit',
      es: 'Comenzar con una Auditoría de Sistemas de Negocio',
    },
    related_service_ids: [
      'service_business_systems_audit',
      'service_workflow_automation',
      'service_custom_business_systems',
    ],
    seo: {
      title: { en: 'AI & Operational Integration — illumi.dev', es: 'IA e Integración Operativa — illumi.dev' },
      description: {
        en: 'Add responsible AI to the parts of the business where it earns its keep.',
        es: 'Añadir IA responsable a las partes del negocio donde justifica su lugar.',
      },
    },
  },

  {
    id: 'service_ongoing_systems_support',
    slug: 'ongoing-systems-support',
    status: 'published',
    order: 60,
    featured: true,
    kicker: { en: '', es: '' },
    title: { en: 'Ongoing Systems Support', es: 'Soporte Continuo de Sistemas' },
    short_description: {
      en: 'Ongoing guidance to help your systems stay useful, documented, and aligned as the business changes.',
      es: 'Acompañamiento continuo para que tus sistemas sigan siendo útiles, estén documentados y se mantengan alineados conforme cambia el negocio.',
    },
    overview: {
      en: 'A system needs attention after launch, not only during the build. We continue working with the operation to review what is working, adjust what is not, and help improvements happen in the right order. The engagement gives a small team ongoing systems guidance without requiring a full-time internal role.',
      es: 'Un sistema necesita atención después del lanzamiento, no solamente durante su desarrollo. Continuamos trabajando con la operación para revisar lo que funciona, ajustar lo que no y ayudar a que las mejoras sucedan en el orden correcto. El servicio ofrece a un equipo pequeño acompañamiento continuo en sistemas sin requerir un puesto interno de tiempo completo.',
    },
    ideal_for: {
      en: 'Small operating teams with active systems that need regular review, prioritization, and experienced guidance as the business evolves.',
      es: 'Equipos operativos pequeños con sistemas activos que necesitan revisión periódica, priorización y acompañamiento con experiencia conforme evoluciona el negocio.',
    },
    problems_addressed: {
      en: [
        'Improvements slow down after launch because nobody owns the next step',
        'Small changes accumulate into unclear decisions later',
        'The team needs an informed outside perspective for decisions that cross systems, operations, and priorities',
        'Priorities drift when nobody is watching them month over month',
      ],
      es: [
        'Las mejoras se ralentizan tras el lanzamiento porque nadie es dueño del siguiente paso',
        'Los cambios pequeños se acumulan y se vuelven decisiones poco claras después',
        'El equipo necesita una perspectiva externa informada para decisiones que abarcan sistemas, operación y prioridades',
        'Las prioridades se desvían cuando nadie las revisa mes a mes',
      ],
    },
    deliverables: {
      en: [
        'Regular working sessions with the operating team',
        'Ongoing review of health, priorities, and open decisions',
        'Documented recommendations after each review cycle',
        'A defined communication channel for questions between scheduled sessions',
      ],
      es: [
        'Sesiones de trabajo regulares con el equipo operativo',
        'Revisión continua de salud, prioridades y decisiones abiertas',
        'Recomendaciones documentadas después de cada ciclo de revisión',
        'Un canal de comunicación definido para preguntas entre las sesiones programadas',
      ],
    },
    outcomes: {
      en: [
        'Steady, visible improvement instead of accumulated drift',
        'Decisions that are documented and easy to revisit',
        'A team that keeps growing its own operating judgment',
      ],
      es: [
        'Mejora estable y visible en lugar de deriva acumulada',
        'Decisiones documentadas y fáciles de revisar',
        'Un equipo que sigue desarrollando su propio criterio operativo',
      ],
    },
    pricing: {
      en: 'Engagements are scoped to your business size, users, locations, workflow complexity, integrations, bilingual requirements, and implementation needs. Start with a Business Systems Audit so we can understand the operation, identify the right priorities, and recommend an appropriate scope.',
      es: 'Cada compromiso se dimensiona según el tamaño del negocio, usuarios, ubicaciones, complejidad del flujo, integraciones, requisitos bilingües y necesidades de implementación. Comienza con una Auditoría de Sistemas de Negocio para entender la operación, identificar las prioridades correctas y recomendar un alcance adecuado.',
    },
    cta_label: {
      en: 'Start with a Business Systems Audit',
      es: 'Comenzar con una Auditoría de Sistemas de Negocio',
    },
    related_service_ids: [
      'service_business_systems_audit',
      'service_workflow_automation',
      'service_custom_business_systems',
    ],
    seo: {
      title: { en: 'Ongoing Systems Support — illumi.dev', es: 'Soporte Continuo de Sistemas — illumi.dev' },
      description: {
        en: 'Continuous experienced guidance so systems keep improving after launch.',
        es: 'Acompañamiento continuo con experiencia para que los sistemas sigan mejorando después del lanzamiento.',
      },
    },
  },
];

export default V2_SERVICES;
